import { Client, User, Conversation, Message } from '@twilio/conversations';
import { ToastrService, ActiveToast } from 'ngx-toastr';
import { from, fromEvent, Observable, Subject, ReplaySubject } from 'rxjs';
import { filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { SnCurrentUser } from '@lib/auth';
import { SnChatToastComponent } from '@lib/toasts';
import { t } from '@lib/translation';
import { FsLiveUpdateSwitchService } from '@lib/transport/live-update-switch';

import { SnPublicity } from '../enums';
import {
  SnMessage,
  SnChannelCollection,
  SnChannel,
  SnChatUser,
  SnChannelSettings,
} from '../models';
import { SnChannelsService } from './channels.service';

@Injectable({ providedIn: 'root' })
export class SnChatService implements OnDestroy {
  get chatIsReady$(): Observable<boolean> {
    return this._chatIsReady$.asObservable();
  }

  get activeChannel(): SnChannel {
    return this._activeChannel;
  }

  get channelsLoaded(): boolean {
    return this._channelsLoaded;
  }

  get channelsLoaded$(): Observable<void> {
    return this._channelsLoaded$.asObservable();
  }

  get channelsUpdated$(): Observable<void> {
    return this._channelsUpdated$.asObservable();
  }

  get activeChannelChanged$(): Observable<SnChannel | null> {
    return this._activeChannelChanged$.asObservable();
  }

  get userIsJoined$(): Observable<void> {
    return this._userJoined$.asObservable();
  }

  get ownTwilioUser(): User {
    return this._chatClient && this._chatClient.user;
  }

  get isTwilioAvailable(): boolean {
    return this._isTwilioAvailable;
  }

  public channels = new SnChannelCollection();

  public dataIsLoading = false;

  public userId: string;
  public isLoading: boolean = false;
  public snoozeToasts: boolean = false;

  private _isTwilioAvailable: boolean;
  private _organizationId: string;
  private _activeChannel: SnChannel;
  private _chatClient: Client;
  private _activeToast: ActiveToast<any>;
  private _duplicateToast: ActiveToast<any>;
  private _channelsLoaded = false;

  private readonly _channelsLoaded$ = new Subject<void>();
  private readonly _channelsUpdated$ = new Subject<void>();
  private readonly _activeChannelChanged$ = new Subject<SnChannel | null>();
  private readonly _userJoined$ = new Subject<void>();

  private readonly _chatIsReady$ = new ReplaySubject<boolean>();
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _liveUpdateSwitchService: FsLiveUpdateSwitchService,
    private readonly _channelsService: SnChannelsService,
    private readonly _currentUser: SnCurrentUser,
    private readonly _sanitizer: DomSanitizer,
  ) {}

  public ngOnDestroy(): void {
    this._chatIsReady$.complete();
    this._channelsLoaded$.complete();
    this._channelsUpdated$.complete();
    this._activeChannelChanged$.complete();
    this._liveUpdateSwitchService.unsubscribeFromChannel(`${this._organizationId}:channels`);

    this._destroy$.next();
    this._destroy$.complete();
  }

  public setOrganizationId(id: string): void {
    this._organizationId = id;
  }

  public setActiveChannel(sid: string, emitEvent: boolean = true): void {
    this._activeChannel = this.channels.getBySid(sid);
    if (emitEvent) {
      this._activeChannelChanged$.next(this._activeChannel);
    }
  }

  public resetActiveChannel(): void {
    this._activeChannel = undefined;
    this._activeChannelChanged$.next();
  }

  public connect(): void {
    this.isLoading = true;
    this._getChatConnection()
      .pipe(
        switchMap(() => this._channelsService.getChannelList()),
        tap(({ results }) => {
          const targetChannels = this._attachTwilioChannels(...results);
          this.channels.clear();
          this.channels.push(...targetChannels);
          this._channelsLoaded = true;
          this._channelsLoaded$.next();
        }),
      )
      .subscribe(() => {
        this._initTwilioSubscriptions();
        this._initLiveUpdateSubscriptions();
        this.isLoading = false;
        this._chatIsReady$.next(true);
      });
  }

  public disconnect(): void {
    if (!this._chatClient) {
      return;
    }

    from(this._chatClient.shutdown()).subscribe(() => {
      this.resetActiveChannel();
      this._destroy$.next();
      this.channels.clear();
      this._liveUpdateSwitchService.unsubscribeFromChannel(`${this._organizationId}:channels`);
      this._chatClient = null;
    });
  }

  public updateChannelList(sid?: string, data?: any): void {
    this.isLoading = true;
    this.channels.clear();
    this._channelsService
      .getChannelList()
      .pipe(takeUntil(this._destroy$))
      .subscribe((res) => {
        const targetChannels = this._attachTwilioChannels(...res.results);
        this.channels.push(...targetChannels);
        this._channelsUpdated$.next();
        this.isLoading = false;
        if (sid && data) {
          this._addChannelMembers(sid, data);
        }
      });
  }

  public getTwilioChannel(sid: string): Observable<Conversation> {
    return from(this._chatClient.getConversationBySid(sid));
  }

  public getTwilioUser(sid: string): Observable<User> {
    return from(this._chatClient.getUser(sid));
  }

  public getSnoozeToast(settings: SnChannelSettings): boolean {
    if (!settings?.snoozeDuration || settings?.snoozeDuration === 0) {
      return false;
    }
    const today = new Date().getTime();
    const snoozedTime = new Date(settings.snoozeCreated).getTime();
    const snoozeAmount = settings.snoozeDuration;
    const difference = (today - snoozedTime) / 60000;
    return difference < snoozeAmount;
  }

  public userJoinedChange(): void {
    this._userJoined$.next();
  }

  private _getChatConnection(): Observable<Client> {
    return this._getToken().pipe(
      map((response: any) => response.token),
      switchMap((token: string) => {
        if (!token) {
          this._isTwilioAvailable = false;
          return;
        }
        this._isTwilioAvailable = true;
        return this._chatClient ? this._refreshToken(token) : this._createClient(token);
      }),
    );
  }

  private _getToken(): Observable<Object> {
    return this._httpClient.get('/api/communications/auth/jwt/obtain/');
  }

  private _createClient(token: string): Observable<Client> {
    return from(Client.create(token)).pipe(
      tap((client: Client) => {
        this._chatClient = client;
        this._tokenExpired()
          .pipe(
            switchMap(() => this._getChatConnection()),
            takeUntil(this._destroy$),
          )
          .subscribe();
      }),
      takeUntil(this._destroy$),
    );
  }

  private _refreshToken(token: string): Observable<Client> {
    return from(this._chatClient.updateToken(token));
  }

  private _initTwilioSubscriptions(): void {
    this._messageAdded()
      .pipe(
        filter((message: SnMessage) => message.twilio.author !== 'system'),
        takeUntil(this._destroy$),
      )
      .subscribe((message: SnMessage) => {
        if (this._activeChannel && message.twilio.conversation.sid === this._activeChannel.sid) {
          this.activeChannel.messages.setUp(message);
          this._updateLastConsumedMessageIndex(this._activeChannel, message);
        } else {
          const channel = this.channels.getBySid(message.twilio.conversation.sid);
          this._sendNotificationMessage(channel, message);
          channel.messages.setUp(message);
          channel.updateUnconsumedMessagesCount();
        }
      });

    this._messageRemoved().subscribe((message: SnMessage) => {
      const channel = this.channels.getBySid(message.twilio.conversation.sid);

      if (channel) {
        channel.messages.delete(message);
      }
    });

    this._messageUpdated().subscribe((message: SnMessage) => {
      const channel = this.channels.getBySid(message.twilio.conversation.sid);

      if (channel) {
        const targetMessage = channel.messages.get(message.id);
        targetMessage.twilio._update(message.twilio);
      }
    });

    this._chatClient.on('tokenAboutToExpire', () => {
      this._getChatConnection().pipe(takeUntil(this._destroy$)).subscribe();
    });
  }

  private _updateLastConsumedMessageIndex(channel: SnChannel, message: SnMessage): void {
    from(channel.twilioChannel.updateLastReadMessageIndex(message.twilio.index))
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        channel.updateUnconsumedMessagesCount();
      });
  }

  private _initLiveUpdateSubscriptions(): void {
    this._liveUpdateSwitchService.initLiveTracking().then(() => {
      this._initChatSubscriptions();
    });
  }

  private _initChatSubscriptions(): void {
    this._liveUpdateSwitchService.subscribeToChannel({
      channel: `${this._organizationId}:channels`,
      callback: ({ name, data }) => {
        switch (name) {
          case 'create:channels':
            this._addChannel(data);
            break;
          case 'update:channels':
            this._updateChannel(data);
            break;
          case 'delete:channels':
            this._deleteChannel(data);
            break;
          case 'join:members':
            this._addChannelMembers(data.channelId, data.members);
            break;
          case 'leave:members':
            this._deleteChannelMembers(data.channelId, data.members);
            break;
        }
      },
    });
  }

  private _addChannel(data: Object): void {
    const channel = new SnChannel(data);
    const isPublicOrganizationChannel =
      channel.isOrganizationChannel && channel.publicity === SnPublicity.Public;

    if (
      !(
        channel.members.findIndex((user) => this._currentUser.id === user.id) > -1 ||
        isPublicOrganizationChannel
      ) ||
      this.channels.has(channel.id)
    ) {
      return;
    }
    channel.users = channel.members;
    this.channels.set(channel);
    this._channelsUpdated$.next();

    this.getTwilioChannel(channel.sid)
      .pipe(takeUntil(this._destroy$))
      .subscribe((twilioChannel: Conversation) => {
        channel.twilioChannel = twilioChannel;
      });
  }

  private _updateChannel(data: Object): void {
    const channel = new SnChannel(data);
    const targetChannel = this.channels.get(channel.id);

    if (!targetChannel) {
      return;
    }
    targetChannel.users = channel.members;
    targetChannel.displayName = channel.displayName;
    targetChannel.publicity = channel.publicity;
    targetChannel.hideSystemNotifications = channel.hideSystemNotifications;
    this._channelsUpdated$.next();
  }

  private _deleteChannel(sid: string): void {
    const channel = this.channels.getBySid(sid);

    if (!channel) {
      return;
    }

    if (this._activeChannel && channel.sid === this._activeChannel.sid) {
      this.resetActiveChannel();
    }

    this.channels.delete(channel);
    this._channelsUpdated$.next();
    this._toastrService.success(t('communication.chat.messages.channelDeleted', { name: channel.displayName }));
  }

  private _addChannelMembers(sid: string, data: Object[]): void {
    const targetChannel = this.channels.getBySid(sid);
    const members = data.map((v) => new SnChatUser(v));
    if (members?.length && this._currentUser.id === members[0].id) {
      this._toastrService.success(t('communication.chat.messages.addedToChannel', { name: targetChannel.displayName }));
    }
    if (!targetChannel) {
      this._channelsService
        .getChannel(sid)
        .subscribe((channel: SnChannel) => this._addChannel(channel));
    } else {
      this.getTwilioChannel(sid).subscribe((tc) => {
        targetChannel.twilioChannel = tc;
        targetChannel.users = [...targetChannel.users, ...members];
        this._channelsUpdated$.next();
      });
    }
  }

  private _deleteChannelMembers(sid: string, data: Object[]): void {
    const channel = this.channels.getBySid(sid);
    if (!channel) {
      return;
    }
    const memberIds = data.map((v) => new SnChatUser(v)).map((u) => u.id);
    channel.users = channel.users.filter((user) => memberIds.indexOf(user.id) === -1);
    this._channelsUpdated$.next();
  }

  private _sendNotificationMessage(channel: SnChannel, message: SnMessage): void {
    this._channelsService
      .getChannelSettings(channel.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe((settings) => {
        this._handleToasts(channel, message, settings);
      });
  }

  // Private chat events
  private _handleToasts(channel: SnChannel, message: SnMessage, settings: any): void {
    if (!channel.isMuted) {
      this.snoozeToasts = this.getSnoozeToast(settings);
      if (this.snoozeToasts) {
        return;
      }
      const messageBody =
        message.twilio.type === 'text' || (message.twilio.type === 'media' && message.twilio.body)
          ? this._sanitizeMessage(message.twilio.body)
          : 'uploaded this file';

      const toastrMessage = channel.isPairChannel
        ? messageBody
        : `<span class="fs__chat-author-name">${this._sanitizeMessage(
            message.twilio.attributes['authorFriendlyName'],
          )}</span>: ${messageBody}`;
      if (this._activeToast) {
        this._duplicateToast = this._toastrService.toasts.find(
          (toast) => toast.toastRef.componentInstance.title === channel.displayName,
        );

        if (this._duplicateToast) {
          this._duplicateToast.toastRef.componentInstance.channel = channel;
          this._duplicateToast.toastRef.componentInstance.chatMessage = message;
        }
      }

      if (!this._duplicateToast) {
        this._activeToast = this._toastrService.show(toastrMessage, channel.displayName, {
          toastComponent: SnChatToastComponent,
          timeOut: 300000,
          extendedTimeOut: 300000,
          tapToDismiss: false,
          progressBar: true,
          closeButton: true,
          enableHtml: true,
          toastClass: 'toast-shadow',
        });

        this._activeToast.toastRef.componentInstance.channel = channel;
        this._activeToast.toastRef.componentInstance.chatMessage = message;
      } else {
        this._duplicateToast.toastRef.componentInstance.toastCount++;
        this._duplicateToast.toastRef.componentInstance.message = toastrMessage;
        this._activeToast.toastRef.componentInstance.chatMessage = message;
      }
      if (message.twilio.type === 'media') {
        this._activeToast.toastRef.componentInstance.getMediaSource();
      }
    }
  }

  private _sanitizeMessage(message: string): string {
    return this._sanitizer.sanitize(SecurityContext.HTML, message) ?? '';
  }

  private _messageAdded(): Observable<SnMessage> {
    return fromEvent(this._chatClient, 'messageAdded').pipe(
      map((message: Message) => new SnMessage(message)),
      takeUntil(this._destroy$),
    );
  }

  private _messageRemoved(): Observable<SnMessage> {
    return fromEvent(this._chatClient, 'messageRemoved').pipe(
      map((message: Message) => new SnMessage(message)),
      takeUntil(this._destroy$),
    );
  }

  private _messageUpdated(): Observable<SnMessage> {
    return fromEvent(this._chatClient, 'messageUpdated').pipe(
      map((data: any) => new SnMessage(data.message)),
      takeUntil(this._destroy$),
    );
  }

  private _tokenExpired(): Observable<Object> {
    return fromEvent(this._chatClient, 'tokenExpired').pipe(takeUntil(this._destroy$));
  }

  private _attachTwilioChannels(...channels: SnChannel[]): SnChannel[] {
    channels.forEach((channel: SnChannel) => {
      this.getTwilioChannel(channel.sid)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (twilioChannel) => {
            channel.twilioChannel = twilioChannel;
          },
          error: () => {},
        });
    });

    return channels.slice();
  }
}
